import type { NxRichTextAlign, NxRichTextMark, NxRichTextToolbarState } from '../RichTextToolbar';
import { caretElement, closestIn, imageWrapOf } from './commands';

export interface NxHtmlEditorFontFamily {
  value: string;
  label: string;
  /** CSS font-family the command writes. */
  stack: string;
}

const MARK_COMMANDS: readonly [NxRichTextMark, string][] = [
  ['bold', 'bold'],
  ['italic', 'italic'],
  ['underline', 'underline'],
  ['strike', 'strikeThrough'],
  ['sup', 'superscript'],
  ['sub', 'subscript'],
];

const BLOCK_SELECTOR = 'p, div, li, h1, h2, h3, h4, h5, h6, blockquote, pre, td, th';

/** `rgb(…)`/`rgba(…)` as `#RRGGBB`; empty for transparent. */
export function cssColorToHex(value: string): string {
  const match = /rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)(?:[\s,/]+([\d.]+%?))?\s*\)/i.exec(value);
  if (!match) return /^#[0-9a-f]{6}$/i.test(value) ? value.toUpperCase() : '';
  if (match[4] !== undefined && parseFloat(match[4]) === 0) return '';
  return `#${[match[1], match[2], match[3]].map((part) => Number(part).toString(16).padStart(2, '0')).join('')}`.toUpperCase();
}

const firstFamily = (stack: string) =>
  stack
    .split(',')[0]
    .trim()
    .replace(/^['"]|['"]$/g, '')
    .toLowerCase();

function familyOf(stack: string, families: readonly NxHtmlEditorFontFamily[]): string {
  const name = firstFamily(stack);
  return (
    families.find((family) => firstFamily(family.stack) === name)?.value ??
    stack
      .split(',')[0]
      .trim()
      .replace(/^['"]|['"]$/g, '')
  );
}

function sizeOf(px: number, sizes: readonly number[]): number {
  const points = px * 0.75;
  const known = sizes.find((size) => Math.abs(size - points) < 0.13);
  return known ?? Math.round(points * 4) / 4;
}

function alignOf(value: string, rtl: boolean): NxRichTextAlign {
  if (value === 'center' || value === '-webkit-center') return 'center';
  if (value === 'justify') return 'justify';
  if (
    value === 'right' ||
    value === '-webkit-right' ||
    (value === 'end' && !rtl) ||
    (value === 'start' && rtl)
  ) {
    return 'right';
  }
  return 'left';
}

function explicit(
  element: Element | null,
  body: HTMLElement,
  read: (el: HTMLElement) => string
): string {
  for (let el = element as HTMLElement | null; el && el !== body; el = el.parentElement) {
    const value = read(el);
    if (value) return value;
  }
  return '';
}

export interface StateInput {
  families: readonly NxHtmlEditorFontFamily[];
  sizes: readonly number[];
  lineSpacings: readonly number[];
  canUndo: boolean;
  canRedo: boolean;
  image: HTMLImageElement | null;
  pasteMode: NxRichTextToolbarState['pasteMode'];
}

/** What the toolbar shows for the caret or selection in the frame. */
export function readState(doc: Document, input: StateInput): NxRichTextToolbarState {
  const view = doc.defaultView!;
  const selection = doc.getSelection();
  const element = caretElement(doc) ?? doc.body;
  const style = view.getComputedStyle(element);
  const block = closestIn(doc, BLOCK_SELECTOR);
  const marks: NxRichTextToolbarState['marks'] = {};
  for (const [mark, command] of MARK_COMMANDS) {
    try {
      marks[mark] = doc.queryCommandState(command);
    } catch {
      marks[mark] = false;
    }
  }
  const link = closestIn(doc, 'a[href]');
  marks.link = !!link;
  const list = closestIn(doc, 'ul, ol');
  const heading = closestIn(doc, 'h1, h2, h3');
  const explicitColor = explicit(element, doc.body, (el) =>
    el.style.color
      ? cssColorToHex(view.getComputedStyle(el).color)
      : (el.getAttribute('color') ?? '')
  );
  const highlight = explicit(element, doc.body, (el) =>
    el.style.backgroundColor ? cssColorToHex(view.getComputedStyle(el).backgroundColor) : ''
  );
  const lineHeight = Number(block?.style.lineHeight);
  const spacing =
    Number.isFinite(lineHeight) && lineHeight > 0 ? Math.round((lineHeight / 1.2) * 100) : NaN;
  return {
    marks,
    align: alignOf(
      (block ? view.getComputedStyle(block) : style).textAlign,
      style.direction === 'rtl'
    ),
    list: list ? (list.localName === 'ol' ? 'orderedList' : 'bulletList') : null,
    heading: heading ? Number(heading.localName.slice(1)) : 0,
    fontFamily: familyOf(style.fontFamily, input.families),
    fontSize: sizeOf(parseFloat(style.fontSize), input.sizes),
    color: cssColorToHex(style.color) || '#000000',
    explicitColor,
    highlight: highlight || '#FFFF00',
    lineSpacing: input.lineSpacings.includes(spacing) ? spacing : null,
    canUndo: input.canUndo,
    canRedo: input.canRedo,
    hasSelection: !!selection && !selection.isCollapsed,
    inTable: !!closestIn(doc, 'td, th'),
    image: input.image ? { wrap: imageWrapOf(input.image) } : null,
    linkHref: link?.getAttribute('href') ?? '',
    pasteMode: input.pasteMode,
  };
}
